import React, { useState } from 'react';
import { Box, Button, Skeleton } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import styled from 'styled-components';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import moment from 'moment';
import { utils, writeFile } from 'xlsx';
import { useStandards } from 'store/story/selectors';
import { useOrgStandardCodes } from 'store/organizations/selectors';
import { formatStandardDisplay } from 'utils/lib/standardHelper';

const ChartWrap = styled.div`
  width: 100%;
  height: 100%;
  min-height: 300px;
  flex: 1;
  background-color: #ffffff;
  border-radius: 4px;
  padding: 8px;
`;

const SkeletonContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
`;

const SkeletonBarsWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  height: calc(100% - 50px);
  gap: 4px;
`;

const SkeletonBar = styled(Skeleton)`
  flex: 1;
  max-width: 80px;
  border-radius: 8px 8px 0 0;
`;

const StandardPassesFailed = ({ optionsData, mostFailedBySchool, value, handleFailedByGrade, loading }) => {
  const standards = useStandards();
  const enabledStateCodes = useOrgStandardCodes();

  const downloadMostFailedGradeCSV = () => {
    try {
      const standardsData = mostFailedBySchool?.overall_standard_failed || [];
      
      if (!standardsData || standardsData.length === 0) {
        return;
      }

      const gradeLabel = value && value !== 'All' ? value.displayName : 'All Grades';
      
      const headers = ['Standard Name', 'Total Failed Questions', 'Grade'];
      const rows = standardsData.map((item) => [
        formatStandardDisplay(item.standard, standards, enabledStateCodes) || item.standard || '-',
        item.count || 0,
        gradeLabel
      ]);

      const wb = utils.book_new();
      const wsData = [headers, ...rows];
      const ws = utils.aoa_to_sheet(wsData);
      
      ws['!cols'] = [
        { wch: 130 },
        { wch: 125 },
        { wch: 115 }
      ];
      
      utils.book_append_sheet(wb, ws, 'Most Standards Failed');
      writeFile(wb, `most_standards_failed_grade_${moment().format('DD-MM-YY_hh-mm-ss')}.csv`);
    } catch (error) {
      console.error('Error in downloading most failed grade CSV:', error);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        margin: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          width: '100%',
          height: '100%',
          gap: 1,
          flex: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            gap: 1,
            marginBottom: 1,
          }}
        >
          <p
            style={{ color: '#000', fontSize: 24, fontWeight: 700, margin: 0, lineHeight: '32px' }}
          >
            <span style={{ display: 'block' }}>Most Standards Failed by Grade</span>
          </p>
        </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, justifyContent: 'flex-end', width: '100%'}}>
            
            <FormControl
              size="small"
              sx={{
                // minWidth: 100,
                maxWidth: 300,
                '& .MuiOutlinedInput-root': {
                  height: 32,
                  paddingRight: '18px',
                  borderRadius: '10px',
                  bgcolor: '#EDEDED',
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
                },
                '& .MuiSelect-select': { py: 0.5, px: 1.5 },
                '& .MuiSelect-icon': { right: 8 },
              }}
            >
              <Select
                value={value}
                displayEmpty
                onChange={(e) => handleFailedByGrade(e.target.value)}
                renderValue={(val) => (val ? val.displayName : 'Grade')}
                inputProps={{ 'aria-label': 'Grade' }}
                MenuProps={{
                  disableScrollLock: true,
                  PaperProps: {
                    sx: {
                      '& .MuiMenuItem-root': {
                        '&:hover': {
                          backgroundColor: '#333333',
                          color: '#ffffff',
                        },
                      },
                      '& .MuiMenuItem-root.Mui-selected': {
                        backgroundColor: 'transparent',
                        '&:hover': {
                          backgroundColor: '#333333',
                          color: '#ffffff',
  
                        },
                      },
                    },
                  },
                }}
              >
               <MenuItem value="All">Grade</MenuItem>
              {optionsData.map((item, index) => (
                <MenuItem value={item} key={index}>
                  {item.displayName}
                </MenuItem>
              ))}
              </Select>
            </FormControl>
            <Button
              onClick={downloadMostFailedGradeCSV}
              size="small"
              sx={{
                minWidth: 'auto',
                width: 28,
                height: 28,
                padding: 0,
                borderRadius: '12px',
                backgroundColor: '#EDEDED',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': { 
                  backgroundColor: '#E0E0E0',
                },
                '&:active': {
                  backgroundColor: '#D5D5D5',
                },
              }}
            >
              <FileDownloadIcon sx={{ fontSize: 18, color: '#61677E' }} />
            </Button>
          </Box>

        <ChartWrap>
          {loading || !mostFailedBySchool ? (
            <SkeletonContainer>
              <SkeletonBarsWrapper>
                {[1, 2, 3, 4, 5, 6].map((_, index) => (
                  <SkeletonBar
                    key={index}
                    variant="rectangular"
                    height={`${(index + 1) * 12 + 20}%`}
                    animation="wave"
                  />
                ))}
              </SkeletonBarsWrapper>
              <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 1 }}>
                {[1, 2, 3, 4, 5, 6].map((_, index) => (
                  <Skeleton
                    key={index}
                    variant="text"
                    width={40}
                    height={20}
                    animation="wave"
                  />
                ))}
              </Box>
            </SkeletonContainer>
          ) : !mostFailedBySchool?.standards || mostFailedBySchool.standards.length === 0 ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '300px',
                color: '#61677E',
              }}
            >
              <Box
                sx={{
                  fontSize: '48px',
                  marginBottom: '16px',
                  opacity: 0.5,
                }}
              >
                📊
              </Box>
              <Box
                sx={{
                  fontSize: '16px',
                  fontWeight: 500,
                  marginBottom: '8px',
                }}
              >
                No Data Available
              </Box>
              {/* <Box
                sx={{
                  fontSize: '14px',
                  color: '#adb5bd',
                  fontWeight: 400,
                }}
              >
                No standards failed data to display
              </Box> */}
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={mostFailedBySchool?.standards || []}
                margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
                barCategoryGap="10%"
                barGap={4}
              >
              <CartesianGrid strokeDasharray="3 3" opacity={0} vertical={false} horizontal={false} />
              <XAxis
                dataKey="standard_name"
                axisLine={true}
                interval={0}
                tickMargin={15}
                angle={-25}
                textAnchor="end"
                tickFormatter={(value) => {
                  const formatted = formatStandardDisplay(value, standards, enabledStateCodes) || value;
                  return formatted && formatted.length > 6 ? `${formatted.substring(0, 6)}...` : formatted;
                }}
                height={40}
                tickLine={false}
                tick={{ fill: '#61677E', fontSize: 'clamp(10px, 2.5vw, 12px)' }}
                stroke="#61677E"
              />
              <YAxis
                axisLine={true}
                //domain={[0, mostFailedBySchool?.total_questions]}
                allowDecimals={false}
                tickLine={false}
                width={30}
                tick={{ fill: '#61677E', fontSize: 'clamp(10px, 2.2vw, 12px)' }}
                stroke="#61677E"
              />
              <Tooltip 
                cursor={{ fill: 'rgba(0,0,0,0.06)' }} 
                formatter={(value) => value}
                labelFormatter={(label) => label}
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const formattedStandard = formatStandardDisplay(payload[0].payload.standard, standards, enabledStateCodes) || payload[0].payload.standard;
                    return (
                      <div style={{
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        padding: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        <p style={{ margin: 0, fontWeight: 600 }}>
                          {formattedStandard}
                        </p>
                        <p style={{ margin: '4px 0 0 0', color: '#716dc2' }}>
                          value: {payload[0].value}
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar
                dataKey="total_failed_questions"
                fill="#716dc2"
                radius={[8, 8, 0, 0]}
                maxBarSize={80}
                
              />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartWrap>
      </Box>
    </Box>
  );
};

export default StandardPassesFailed;
